import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API } from '@/config';
import { SiteHeader } from '@/components/SiteHeader';
import axios from 'axios';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setError('Post not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading...</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Link to="/blog" className="text-sm text-[#10B981] hover:underline">Back to blog</Link>
      </div>
    </div>
  );

  // Render markdown-like content (basic: headings, bold, lists, links, paragraphs)
  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;
      if (trimmed.startsWith('### ')) return <h3 key={i} className="text-lg font-semibold text-foreground mt-6 mb-2">{trimmed.slice(4)}</h3>;
      if (trimmed.startsWith('## ')) return <h2 key={i} className="text-xl font-bold text-foreground mt-8 mb-3">{trimmed.slice(3)}</h2>;
      if (trimmed.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold text-foreground mt-8 mb-3">{trimmed.slice(2)}</h1>;
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) return <li key={i} className="text-sm text-muted-foreground ml-4 mb-1 list-disc">{formatInline(trimmed.slice(2))}</li>;
      if (/^\d+\.\s/.test(trimmed)) return <li key={i} className="text-sm text-muted-foreground ml-4 mb-1 list-decimal">{formatInline(trimmed.replace(/^\d+\.\s/, ''))}</li>;
      if (trimmed.startsWith('> ')) return <blockquote key={i} className="border-l-4 border-[#10B981] pl-4 my-3 italic text-muted-foreground text-sm">{trimmed.slice(2)}</blockquote>;
      if (trimmed.startsWith('---')) return <hr key={i} className="border-border my-6" />;
      return <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{formatInline(trimmed)}</p>;
    });
  };

  const formatInline = (text) => {
    // Bold: **text**
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      {/* SEO meta (set via document.title) */}
      {post && <title>{post.meta_title || post.title} — Whisper.AI Blog</title>}

      <SiteHeader />

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Cover image */}
        {post.cover_image && (
          <div className="w-full h-64 sm:h-80 rounded-2xl overflow-hidden mb-8 bg-muted">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
          {post.tags?.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <Tag className="h-3 w-3" />
              {post.tags.map((tag, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-[10px] font-medium">{tag}</span>
              ))}
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground leading-tight mb-6" data-testid="blog-post-title">
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="text-base text-muted-foreground leading-relaxed mb-8 border-l-4 border-[#10B981]/30 pl-4">{post.excerpt}</p>
        )}

        {/* Content */}
        <article className="prose-whisper" data-testid="blog-post-content">
          {renderContent(post.content)}
        </article>

        {/* CTA */}
        <div className="mt-12 p-8 rounded-2xl bg-[#18181B] text-center">
          <h3 className="text-lg font-bold text-white mb-2">Ready to get started?</h3>
          <p className="text-sm text-neutral-400 mb-4">Deploy your AI chat widget in 60 seconds. Free plan available.</p>
          <Link to="/signup" className="inline-flex px-6 py-3 rounded-xl bg-[#10B981] text-white text-sm font-medium hover:bg-[#059669] transition-colors">
            Start Free
          </Link>
        </div>

        {/* Back to blog */}
        <div className="mt-8 text-center">
          <Link to="/blog" className="text-sm text-[#10B981] hover:underline flex items-center gap-1 justify-center">
            <ArrowLeft className="h-4 w-4" /> Back to all posts
          </Link>
        </div>
      </main>

      <footer className="border-t border-border py-6 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Whisper.AI &mdash; Powered by WPO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">Home</Link>
        </div>
      </footer>
    </div>
  );
}
